import EverTrue from "app";
import { useState, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Loading, ModalTrigger } from "@evertrue/et-components";
import EmailBody from "./email-body";
import Api from "entities/helpers/api";
import { MultiRecipientsInput } from "./multi-recipients-input";
import { useIdentity } from "base/identity-resolver";
import { createAction } from "apps/tasks/utils/tasks-actions";
import { setTimestampToMidnightAndRemoveMilliseconds } from "apps/journeys/utils/helpers";
import EmailTemplatesModalController from "apps/outreach/email/templates/controllers/email-templates-modal-controller";
import { trackOutreachAction } from "apps/outreach/utils/utils";

export const EmailForm = ({ recipient, handleClose = () => {}, setDrawerTitle = () => {}, contactId }) => {
  const defaultEmails = recipient ? [recipient] : [];
  const [inputRecipients, setInputRecipients] = useState(defaultEmails);
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const { user, org } = useIdentity();

  const quillRef = useRef();

  const setValue = ({ target: { value } }, setFunction) => {
    setFunction(value);
  };

  const handleChangeSubject = (e) => {
    setValue(e, setSubject);
    setTimeout(() => setDrawerTitle(e.target.value), "3000");
  };

  const handleSubmit = () => {
    trackOutreachAction("send_email");
    const today = setTimestampToMidnightAndRemoveMilliseconds(new Date().getTime());
    setIsSending(true);
    createAction({
      contact_id: contactId,
      date_occurred: today,
      outreach_method: "EMAIL",
      oid: org.id,
      completed_by_id: user.id,
      creator_user_id: user.id,
      updater_user_id: user.id,
    });

    Api.OUTREACH.SEND.post({
      data: JSON.stringify({
        message: {
          to: inputRecipients.map((email) => ({ email })),
          subject,
          body: emailBody,
        },
      }),
    })
      .then(() => {
        EverTrue.Alert.success("Email sent");
        setDrawerTitle("");
        handleClose();
      })
      .catch(() => {
        EverTrue.Alert.error("There was a problem sending your email. Please try again.");
        setIsSending(false);
      });
  };

  return (
    <div className="email-form email-editor">
      <div style={{ overflow: "auto", maxHeight: "370px" }}>
        <div className="email-form-header">
          <div className="email-form-header-field">
            <label className="email-form--label">
              <strong className="email-form-header-label">To:</strong>
              <MultiRecipientsInput inputRecipients={inputRecipients} setInputRecipients={setInputRecipients} />
            </label>
            <hr className="email-form-header-hr" />
          </div>
          <div className="email-form-header-field email-subject">
            <label className="space-between email-form--label">
              <strong>Subject:</strong>
              <input
                className="email-form--input-field"
                style={{ marginLeft: "5px" }}
                value={subject}
                onChange={handleChangeSubject}
                data-testid="subject-field"
              />
            </label>
            <hr className="email-form-header-hr" />
          </div>
        </div>
        <div className="email-form--body" data-testid="body-field">
          {isSending ? (
            <Loading />
          ) : (
            <EmailBody emailBody={emailBody} setEmailBody={setEmailBody} quillRef={quillRef} />
          )}
        </div>
      </div>
      <hr className="email-form-header-hr" style={{ bottom: "69px" }} />
      <div className="email-form--footer space-between">
        <Button label="sendButton" type="default" onClick={handleSubmit} isDisabled={isSending}>
          Send
        </Button>
        <ModalTrigger
          size="1000px"
          closeOnOutsideClick={true}
          buttonProps={{ type: "secondary" }}
          buttonText="Email Templates"
        >
          {({ closeModal }) => (
            <EmailTemplatesModalController closeModal={closeModal} emailBodyRef={quillRef} setSubject={setSubject} />
          )}
        </ModalTrigger>
      </div>
    </div>
  );
};

EmailForm.propTypes = {
  recipient: PropTypes.string,
  handleClose: PropTypes.func,
  setDrawerTitle: PropTypes.func,
  contactId: PropTypes.number,
};

EmailForm.defaultProps = {
  recipient: "",
  handleSubmit: () => {},
};

export default EmailForm;
